/**
 * Utilidades para reubicación por DUN (caja cerrada con N unidades de un mismo SKU).
 *
 * El inventario y el endpoint de reubicación siempre trabajan en unidades base del
 * SKU. El operador, en cambio, escanea y cuenta cajas. Estas funciones son la única
 * frontera entre ambas magnitudes: la UI muestra cajas, el backend recibe unidades.
 *
 * Misma regla de truncado que ya usa el picking (ver PickingProduct.tsx): las cajas
 * incompletas no se muestran ni se pueden mover.
 */

/** Un DUN tiene exactamente 14 caracteres. Misma validación que el mantenedor (CreateDun.tsx). */
export const DUN_CODE_LENGTH = 14

/**
 * Un DUN puede ser alfanumérico (p. ej. GZ3041560AA034), así que por su forma no se
 * distingue de un SKU: lo único que se puede afirmar es que un código de largo
 * distinto de 14 seguro NO es un DUN. Por eso el DUN se resuelve como último
 * recurso, recién cuando la búsqueda por sku y por ean13 no encontró nada — así un
 * código que existe como SKU nunca queda tapado por un DUN homónimo.
 */
export const isDunCandidate = (code: string): boolean =>
  code.trim().length === DUN_CODE_LENGTH

/**
 * Cajas completas que se pueden armar con `units` unidades.
 * Trunca hacia abajo: 104 unidades con DUN de 6 son 17 cajas, no 17,33.
 */
export const toDunPacks = (units: number, dunQty?: number): number =>
  dunQty && dunQty > 0 ? Math.floor(units / dunQty) : 0

/** Unidades base que representan `packs` cajas. */
export const toBaseUnits = (packs: number, dunQty?: number): number =>
  dunQty && dunQty > 0 ? packs * dunQty : packs

/**
 * Unidades sueltas que quedan fuera de las cajas completas (104 % 6 = 2).
 * No son reubicables por DUN; se calculan para poder informarlas.
 */
export const dunRemainder = (units: number, dunQty?: number): number =>
  dunQty && dunQty > 0 ? units % dunQty : 0

/**
 * Etiqueta del producto: en modo DUN el SKU va acompañado de la unidad de medida y
 * las unidades que contiene la caja — "PACK (X6) - SKUSIMPLE001".
 *
 * Vive acá y no en cada pantalla para que el header y la confirmación no se
 * desincronicen: es la misma referencia que el operador sigue durante todo el flujo.
 */
export const formatSkuLabel = (state: {
  sku: string
  isDun?: boolean
  dunQty?: number
  typePackageName?: string
}): string =>
  state.isDun
    ? `${state.typePackageName || 'Caja'} (X${state.dunQty}) - ${state.sku}`
    : state.sku
